/* eslint-disable no-nested-ternary, react/require-default-props */

import React, { PropsWithChildren } from 'react';
import ButtonText from 'components/Typography/ButtonText';
import ButtonSmall from 'components/Typography/ButtonSmall';
import Icon from 'components/DesignSystem/Icon';
import { IconType } from 'components/DesignSystem/Icon/icons';

import './index.scss';

export type ButtonType = 'primary' | 'secondary' | 'tertiary';
export type ButtonIconPlacement = 'leading' | 'trailing';

export interface ButtonProps {
  icon?: IconType | undefined;
  submit?: boolean;
  disabled?: boolean;
  type?: ButtonType;
  small?: boolean;
  iconPlacement?: ButtonIconPlacement;
  className?: string;
  id?: string;
  title?: string;
  'aria-label'?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  role?: string;
  'aria-selected'?: boolean;
  /** Estado de um disclosure (ex.: chevron que expande a linha). */
  'aria-expanded'?: boolean;
  /** `id` do elemento controlado pelo botão (par do `aria-expanded`). */
  'aria-controls'?: string;
  /** `id` do elemento que descreve o botão (ex.: texto da dica). */
  'aria-describedby'?: string;
  /**
   * Desabilita sem tirar o foco: o botão continua na ordem de Tab (a dica do
   * `TooltipWrapper` aparece pelo teclado), ignora o clique e recebe o visual de
   * desabilitado. Prefira a `disabled` quando a ação não precisa de explicação.
   * Como o botão segue focável, o `keydown` de Enter e Espaço ainda sobe para o pai:
   * um ancestral com `onKeyDown` (ex.: linha que expande com Enter) deve conferir
   * `event.target === event.currentTarget`.
   */
  'aria-disabled'?: boolean;
}

/**
 * Bloqueia o clique de um botão com `aria-disabled`, igualando o `disabled` nativo:
 * não chama `onClick`; com `preventDefault`, não envia o formulário quando o botão é
 * de submit; e com `stopPropagation`, o clique não sobe para ancestrais clicáveis
 * (ex.: `Accordion.Header`, linha de tabela com `onClick`).
 */
function preventAriaDisabledClick(event: React.MouseEvent<HTMLButtonElement>): void {
  event.preventDefault();
  event.stopPropagation();
}

export default function Button({
  children,
  icon = undefined,
  submit = false,
  disabled = false,
  type = 'primary',
  small = false,
  iconPlacement = 'leading',
  className = '',
  id = undefined,
  title = '',
  'aria-label': ariaLabel = undefined,
  onClick,
  role: roleProp,
  'aria-selected': ariaSelected,
  'aria-expanded': ariaExpanded,
  'aria-controls': ariaControls,
  'aria-describedby': ariaDescribedBy,
  'aria-disabled': ariaDisabled,
}: PropsWithChildren<ButtonProps>) {
  // Sem aria-disabled a string fica idêntica à de hoje (snapshots dos consumidores).
  const extraClassName = ariaDisabled ? `ds-button-disabled ${className}`.trim() : className;

  return (
    <button
      type={submit ? 'submit' : 'button'}
      id={id}
      title={title}
      aria-label={ariaLabel}
      disabled={disabled}
      role={roleProp}
      aria-selected={ariaSelected}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-describedby={ariaDescribedBy}
      aria-disabled={ariaDisabled}
      className={`
        ds-button-style ${small ? 'ds-button-small' : ''} ${type === 'primary' ? 'ds-button-primary' : ''}
        ${type === 'secondary' ? 'ds-button-secondary' : ''} ${type === 'tertiary' ? 'ds-button-tertiary' : ''}
        ${icon ? children ? iconPlacement === 'leading' ? 'icon-leading' : 'icon-trailing' : 'icon-only' : ''}
        ${extraClassName}
      `}
      onClick={ariaDisabled ? preventAriaDisabledClick : onClick}
    >
      {iconPlacement === 'leading' && icon && (
        <Icon iconType={icon} className={children ? 'ds-button-icon me-2' : 'ds-button-icon'} />
      )}
      {children && (small
        ? (<ButtonSmall>{children}</ButtonSmall>)
        : (<ButtonText>{children}</ButtonText>))}
      {iconPlacement === 'trailing' && icon && (
        <Icon iconType={icon} className={children ? 'ds-button-icon ms-2' : 'ds-button-icon'} />
      )}
    </button>
  );
}
